import React, { useContext, useEffect, useRef, useState } from 'react';
import axios from "axios";
import { useLocation } from 'react-router-dom';
import ReCAPTCHA from "react-google-recaptcha";
import { SettingsContext, UserContext } from '../../contexts';
import { Alert, OTPVerification } from "../../components"
import './step3.module.scss';

const RECAPTCHA_SITE_KEY = import.meta.env.VITE_RECAPTCHA_SITE_KEY;

/**
 * Retail Circle verifies by SMS OTP; DAO Watches verifies by email. They share
 * nothing but the sign-up state they are handed.
 *
 * The two used to be `if (isRetailCircle) { ...hooks... }` inside one component,
 * which put five hooks behind a condition — the exact shape React cannot allow,
 * since a branch that renders a different NUMBER of hooks corrupts the hook
 * order. Splitting them means each screen's hooks are unconditional in their own
 * component, and the branch happens where it belongs: at the render.
 */
const RetailCircleOtpStep = ({ phone }) => {
  const authentication = useContext(UserContext);
  const [status, setStatus] = useState(null);
  const [otpSent, setOtpSent] = useState(false);

  // Send once, on arrival. `otpSent` is in the deps AND checked inside: the
  // check is what stops a second send when the flag flips.
  useEffect(() => {
    if (!phone || otpSent) return;

    const sendOtp = async () => {
      try {
        await axios.post('/api/send-otp', { phoneNumber: phone });
        setOtpSent(true);
      } catch (err) {
        setStatus('error');
      }
    };

    sendOtp();
  }, [phone, otpSent]);

  const handleOtpVerified = async (otpCode) => {
    // Verify OTP + get tokens via signin-with-tokens (single step)
    const response = await axios.post('/app_api/rc-users/signin-with-tokens', {
      phoneNumber: phone,
      otpCode,
      deviceId: 'rc-webapp-' + Date.now(),
    });

    if (!response.data.success) {
      throw new Error(response.data.message || 'OTP verification failed.');
    }

    const userData = response.data.user;
    const authToken = response.data.token;

    // Signed in either way: an unapproved account still logs in and then meets
    // the approval-pending gate.
    authentication.login(userData, authToken, true);
  };

  if (!phone) {
    return (
      <div className="create-account-step-3">
        <Alert type="error" message="Error" description="Phone number not found. Please go back and try again." />
      </div>
    );
  }

  return (
    <div className="create-account-step-3">
      {status === 'error' && (
        <Alert type="error" message="Error" description="Failed to send OTP. Please try again." />
      )}
      <OTPVerification
        phoneNumber={phone}
        onVerified={handleOtpVerified}
      />
    </div>
  );
};

const DaoEmailStep = ({ user }) => {
  const [status, setStatus] = useState(null);
  const recaptchaRef = useRef();

  const resendEmail = async () => {
    const token = await recaptchaRef.current.executeAsync("welcome");

    await axios.post('/api/email-verifications', {
        type: 'welcome',
        email: user.email,
        recaptcha: token
    });

    recaptchaRef.current?.reset();
    setStatus('success')
  };

  useEffect(() => {
    if (status === 'success') {
      setTimeout(() => setStatus(null), 5000);
    }
  }, [status]);

  return (
    <div className="create-account-step-3">
      <ReCAPTCHA
        ref={recaptchaRef}
        size="invisible"
        sitekey={RECAPTCHA_SITE_KEY}
      />

      {status === 'success' && (
        <Alert type="success"
          message="Submission successful"
          clasName="mb-4"
          description="An email has been sent to your email address with further instructions."
        />
      )}

      <div className="title">Verify your email</div>
      <div className="content">
        To verify, open the link we emailed to {user.email}.&nbsp;
        <a onClick={() => resendEmail()}>Click here</a> to re-send it.
      </div>
    </div>
  );
};

const Step3 = () => {
  const { isRetailCircle } = useContext(SettingsContext);
  const location = useLocation();

  const { user, phone } = location.state || { user: { email: '' }, phone: '' };

  if (!user) {
    return null;
  }

  return isRetailCircle
    ? <RetailCircleOtpStep phone={phone} />
    : <DaoEmailStep user={user} />;
};

export default Step3;
